"use client"

import { useState, useEffect, useCallback } from "react"
import classNames from "classnames"
import withStyles from "@material-ui/core/styles/withStyles"
import Parallax from "components/Parallax/Parallax.jsx"
import GridContainer from "components/Grid/GridContainer.jsx"
import GridItem from "components/Grid/GridItem.jsx"
import Button from "components/CustomButtons/Button.jsx"
import Card from "components/Card/Card.jsx"
import CardBody from "components/Card/CardBody.jsx"
import Typography from "@material-ui/core/Typography"

import shoppingCartStyle from "assets/jss/material-kit-pro-react/views/shoppingCartStyle.jsx"
import borderImage from "assets/img/border.svg"
import Cookies from "js-cookie"
import Snackbar from "components/Snackbar/Snackbar.jsx"
import ModalExito from "./ModalExito"
import CheckoutEmbedded from "./CheckoutEmbedded.jsx"

import { EnviarMail, ContenidoMail } from "../Generales/Controles/EnvioMail"
import { cartApi } from "api/cart/index.js"
import { useUserStore } from "stores/userStore"
import { useOrderStore } from "stores/orderStore"

const ShoppingCartPage = ({ classes }) => {
	const { orderState, calculateTotals, clearOrder } = useOrderStore()
	const { userState, setUserState } = useUserStore()
	// Estados
	const [verModalExito, setVerModalExito] = useState(false)
	const [imagenCargo, setImagenCargo] = useState("")
	const [verModalCompra, setVerModalCompra] = useState(false)
	const [listaCarrito, setListaCarrito] = useState([])
	const [nombreAtributo, setNombreAtributo] = useState(0)
	const [cargando, setCargando] = useState(false)
	const [datosCompra, setDatosCompra] = useState(null)
	const [checkoutData, setCheckoutData] = useState(null)
	const [showCheckout, setShowCheckout] = useState(false)

	// Estados para notificaciones
	const [colorNotificacion, setColorNotificacion] = useState("")
	const [mensaje, setMensaje] = useState("")
	const [br, setBr] = useState(false)

	useEffect(() => {
		window.scrollTo(0, 0)
		document.body.scrollTop = 0

		const datosGuardados = orderState.checkoutData

		if (datosGuardados == undefined || datosGuardados == null) {
			window.location.href = "/carrito"
			return
		}

		setDatosCompra(datosGuardados)
		setListaCarrito(datosGuardados.Productos)

		// Inicializar checkout automáticamente si hay productos
		if (orderState.totals.itemCount > 0) {
			setVerModalCompra(true) // Mostrar loader mientras se inicializa el checkout
			crearCheckout()
		}
	}, [])

	// Cambiar formato de precio
	const cambiarFormatoPrecio = (precio) => {
		return (
			"$" +
			Number.parseFloat(precio || 0, 10)
				.toFixed(2)
				.replace(/(\d)(?=(\d{3})+\.)/g, "$1,")
				.toString()
		)
	}

	// Mostrar notificación
	const showNotification = useCallback((mensaje, color) => {
		setColorNotificacion(color)
		setMensaje(mensaje)
		setBr(true)

		setTimeout(() => {
			setBr(false)
		}, 3000)
	}, [])

	// Crear checkout
	const crearCheckout = useCallback(async () => {
		const productos = []

		orderState.items.forEach((m) => {
			productos.push({
				_id: m.idModelo,
				Diseno: m.desing.id,
				Cantidad: m.Cantidad,
				ComisionEntregada: false,
				NombreProducto: m.product.description,
			})
		})

		// Validaciones antes de crear el checkout
		const saldoAplicado = orderState.totals.balanceApplied
		const total = orderState.totals.total
		const descuentoCupon = orderState.cupon?.aplicado ? (orderState.cupon.descuentoAplicable || 0) : 0
		const codigoCupon = orderState.cupon?.aplicado ? (orderState.cupon.codigo || null) : null
		// El descuento de reglas automáticas ya está incluido en orderState.totals.discount si no hay cupón manual
		const descuentoReglasAutomaticas = (!orderState.cupon?.aplicado && orderState.reglasAutomaticas && orderState.reglasAutomaticas.length > 0) 
			? orderState.reglasAutomaticas.reduce((sum, regla) => sum + (regla.descuentoAplicable || 0), 0)
			: 0

		// Validar que el monto a pagar sea mayor a 0
		if (total <= 0) {
			setVerModalCompra(false)
			if (total === 0) {
				showNotification("El total de la compra es $0.00. No se puede procesar el pago.", "danger")
			} else {
				showNotification("El saldo aplicado cubre todo el total. No se requiere pago adicional.", "warning")
			}
			return
		}

		// Validar que el total sea mayor a 0
		if (total <= 0) {
			setVerModalCompra(false)
			showNotification("El total de la compra debe ser mayor a $0.00", "danger")
			return
		}

		// Preparar payload para checkout embebido
		const payload = {
			idUser: userState.id,
			SaleData: orderState.checkoutData,
			Products: productos,
			Address: orderState.address,
			BalancePaymentAmount: saldoAplicado,
			CuponDiscountAmount: descuentoCupon, // Agregar descuento del cupón
			CuponCodigo: codigoCupon, // Agregar código del cupón
			Total: total,
			PaymentMethod: [0, 1, 2],
			useCheckout: {
				enabled: true,
				type: "embedded",
			},
		}

		const response = await cartApi.confirmShipping(payload)

		if (!response.success) {
			setVerModalCompra(false)
			showNotification(response.message, "danger")
		} else {
			// Verificar si la respuesta es de checkout
			if (response.data?.checkoutRequestId || response.data?.checkoutId) {
				const checkoutRequestId = response.data.checkoutRequestId || response.data.checkoutId
				const checkoutType = response.data.type || "embedded"

				// Si es redireccionado, redirigir inmediatamente
				if (checkoutType === "redirected" && response.data.checkoutUrl) {
					window.location.href = response.data.checkoutUrl
					return
				}

				// Si es embebido, mostrar el componente
				if (checkoutType === "embedded") {
					setVerModalCompra(false)
					setCheckoutData({
						orderId: response.data.orderId,
						folioPedido: response.data.folioPedido,
						checkoutRequestId: checkoutRequestId,
						type: checkoutType,
					})
					setShowCheckout(true)
				} else {
					setVerModalCompra(false)
					showNotification("No se pudo crear el checkout. Intenta de nuevo.", "danger")
				}
			} else {
				setVerModalCompra(false)
				showNotification("No se pudo crear el checkout. Intenta de nuevo.", "danger")
			}
		}
	}, [datosCompra, showNotification])

	// Cancelar compra - NO limpiar el carrito, solo regresar para que pueda volver a intentar
	const cancel = () => {
		// NO limpiar el carrito aquí, permitir que el usuario regrese y pueda volver a intentar
		window.location.href = "/carrito"
	}
	// Manejar éxito del checkout (tarjeta pagada o referencia SPEI/OXXO generada)
	const handleCheckoutSuccess = async (orderId, paymentInfo = {}) => {
		setShowCheckout(false)
		setVerModalCompra(true)

		const isPending = paymentInfo.pending === true
		showNotification(
			isPending
				? "Checkout generado. Quitando del carrito lo que acabas de comprar..."
				: "Pago procesado exitosamente. Procesando tu orden...",
			"success"
		)

		try {
			if (userState.id) {
				await cartApi.clearPurchasedItems({
					orderId,
					idUser: userState.id,
					folioPedido: checkoutData?.folioPedido,
					cartIds: orderState.selectedItems || [],
				})
			}
		} catch (error) {
			console.error("Error al limpiar el carrito después del checkout:", error)
		}

		setTimeout(() => {
			setVerModalCompra(false)
			setVerModalExito(true)
			clearOrder()
			setUserState({ Basket: 0 })
		}, 3000)
	}

	// Manejar error del checkout
	const handleCheckoutError = (error) => {
		setShowCheckout(false)
		setVerModalCompra(false)
		showNotification(error || "Error al procesar el pago", "danger")
	}


	return (
		<div>
			<Parallax
				image={borderImage}
				style={{
					height: "20vh",
					overflow: "hidden",
				}}
			>
				<div className={classes.container}>
					<GridContainer>
						<GridItem md={8} sm={8} className={classNames(classes.mlAuto, classes.mrAuto, classes.textCenter)} />
					</GridContainer>
				</div>
			</Parallax>
			<Parallax
				style={{
					height: "10vh",
					overflow: "hidden",
				}}
			/>
			<div className={classes.container}>
				{/* Mostrar checkout embebido si está activo */}
				{showCheckout && checkoutData ? (
					<GridContainer>
						<GridItem xs={12} sm={12} md={12} lg={12}>
							<Button
								onClick={() => {
									window.location.href = "/carrito"
								}}
								size="sm"
								color="default"
								style={{ marginBottom: "20px" }}
							>
								← Volver al carrito
							</Button>
							<CheckoutEmbedded
								checkoutRequestId={checkoutData.checkoutRequestId}
								orderId={checkoutData.orderId}
								onSuccess={handleCheckoutSuccess}
								onError={handleCheckoutError}
							/>
						</GridItem>
					</GridContainer>
				) : verModalCompra ? (
					/* Contenedor de carga estilizado para checkout */
					<GridContainer>
						<GridItem xs={12} sm={12} md={8} lg={6} style={{ margin: "0 auto" }}>
							<Card style={{ 
								boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
								borderRadius: "12px",
								textAlign: "center",
								padding: "60px 40px"
							}}>
								<CardBody>
									<div style={{ marginBottom: "30px" }}>
										{/* Animación de tarjeta de crédito */}
										<div style={{
											width: "120px",
											height: "80px",
											margin: "0 auto 30px",
											position: "relative",
											perspective: "1000px"
										}}>
											<div style={{
												width: "100%",
												height: "100%",
												background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
												borderRadius: "12px",
												position: "relative",
												boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)",
												animation: "cardFloat 3s ease-in-out infinite",
												transformStyle: "preserve-3d"
											}}>
												{/* Chip de tarjeta */}
												<div style={{
													position: "absolute",
													top: "20px",
													left: "20px",
													width: "30px",
													height: "25px",
													background: "rgba(255,255,255,0.3)",
													borderRadius: "4px",
													backdropFilter: "blur(10px)"
												}}></div>
												{/* Líneas de la tarjeta */}
												<div style={{
													position: "absolute",
													bottom: "15px",
													left: "20px",
													right: "20px",
													height: "4px",
													background: "rgba(255,255,255,0.6)",
													borderRadius: "2px"
												}}></div>
												<div style={{
													position: "absolute",
													bottom: "25px",
													left: "20px",
													width: "60%",
													height: "4px",
													background: "rgba(255,255,255,0.4)",
													borderRadius: "2px"
												}}></div>
											</div>
										</div>
										
										{/* Spinner de carga */}
										<div style={{
											width: "50px",
											height: "50px",
											margin: "0 auto 20px",
											border: "4px solid #f3f3f3",
											borderTop: "4px solid #667eea",
											borderRadius: "50%",
											animation: "spin 1s linear infinite"
										}}></div>
									</div>
									
									<Typography variant="h5" style={{ 
										fontWeight: 600, 
										marginBottom: "15px",
										color: "#333"
									}}>
										Preparando tu pago seguro
									</Typography>
									
									<Typography variant="body1" style={{ 
										color: "#666",
										marginBottom: "10px"
									}}>
										Te estamos redirigiendo al checkout...
									</Typography>
									
									<div style={{
										display: "flex",
										justifyContent: "center",
										gap: "8px",
										marginTop: "20px"
									}}>
										<div style={{
											width: "8px",
											height: "8px",
											borderRadius: "50%",
											background: "#667eea",
											animation: "dotPulse 1.4s ease-in-out infinite",
											animationDelay: "0s"
										}}></div>
										<div style={{
											width: "8px",
											height: "8px",
											borderRadius: "50%",
											background: "#667eea",
											animation: "dotPulse 1.4s ease-in-out infinite",
											animationDelay: "0.2s"
										}}></div>
										<div style={{
											width: "8px",
											height: "8px",
											borderRadius: "50%",
											background: "#667eea",
											animation: "dotPulse 1.4s ease-in-out infinite",
											animationDelay: "0.4s"
										}}></div>
									</div>
								</CardBody>
							</Card>
							
							{/* Espaciador para mantener el footer abajo */}
							<div style={{ height: "400px" }}></div>
						</GridItem>
					</GridContainer>
				) : !verModalCompra ? (
					<GridContainer>
						<GridItem xs={12} sm={12} md={12} lg={8}>
							<Card>
								<CardBody>
									<GridContainer>
										<GridItem>
											<h4>Resumen de compra</h4>
										</GridItem>
										<GridItem>
											<hr />
										</GridItem>
									</GridContainer>
									<GridContainer>
										<GridItem className={`${classes.textRight}`} xs={6} sm={5} md={5} lg={5}>
											<span>Productos ({orderState.totals.itemCount}): </span>
											<br />
											<br />
											{(orderState.cupon?.aplicado || (orderState.reglasAutomaticas && orderState.reglasAutomaticas.length > 0)) && orderState.totals.discount > 0 && (
												<>
													<span style={{ color: "#4caf50" }}>Descuento: </span>
													<br />
													<br />
												</>
											)}
											<span>Envío: </span>
											<br />
											<br />
											{orderState.totals.balanceApplied > 0 && (
												<>
													<span>Saldo aplicado: </span>
													<br />
													<br />
												</>
											)}
											<br />
											<br />
											<span>Total: </span>
										</GridItem>
										<GridItem className={`${classes.textRight}`} xs={4} sm={5} md={4} lg={5}>
											<span>{cambiarFormatoPrecio(orderState.totals.subtotal)}</span>
											<br />
											<br />
											{(orderState.cupon?.aplicado || (orderState.reglasAutomaticas && orderState.reglasAutomaticas.length > 0)) && orderState.totals.discount > 0 && (
												<>
													<span style={{ color: "#4caf50" }}>-{cambiarFormatoPrecio(orderState.totals.discount)}</span>
													<br />
													<br />
												</>
											)}
											<span>{cambiarFormatoPrecio(orderState.totals.shipping)}</span>
											<br />
											<br />
											{orderState.totals.balanceApplied > 0 && (
												<>
													<span>{cambiarFormatoPrecio(orderState.totals.balanceApplied)}</span>
													<br />
													<br />
												</>
											)}
											<br />
											<br />
											<span>{cambiarFormatoPrecio(orderState.totals.total)}</span>
											<br />
											<br />
											<br />
										</GridItem>
									</GridContainer>
									<GridContainer>
										<GridItem lg={12}>
											<hr />
											<Typography variant="body2" color="textSecondary" gutterBottom>
												El pago se procesará de forma segura usando Conekta Checkout.
											</Typography>
										</GridItem>
									</GridContainer>
									<GridContainer>
										<GridItem className={`${classes.textCenter}`} lg={12}>
											<Button id="btnCancel" onClick={cancel} size="md" color="danger">
												CANCELAR
											</Button>
										</GridItem>
									</GridContainer>
								</CardBody>
							</Card>
						</GridItem>
					</GridContainer>
				) : null}
				
				{/* Estilos CSS para las animaciones */}
				<style>{`
					@keyframes spin {
						0% { transform: rotate(0deg); }
						100% { transform: rotate(360deg); }
					}
					
					@keyframes cardFloat {
						0%, 100% { 
							transform: translateY(0px) rotateY(0deg);
						}
						50% { 
							transform: translateY(-10px) rotateY(5deg);
						}
					}
					
					@keyframes dotPulse {
						0%, 80%, 100% {
							transform: scale(0.8);
							opacity: 0.5;
						}
						40% {
							transform: scale(1.2);
							opacity: 1;
						}
					}
				`}</style>
				<ModalExito VerModal={verModalExito} FormaDePago={0} Correo={datosCompra?.customer_info?.email || ""} />
				<Snackbar
					place="tc"
					color={colorNotificacion}
					message={mensaje}
					open={br}
					closeNotification={() => setBr(false)}
					close
				/>
			</div>
		</div>
	)
}

export default withStyles(shoppingCartStyle)(ShoppingCartPage)
